
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, CreditCard, Package, User, Mail, Printer, Eye, Edit } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import { useState } from "react";
import PrintableOrderSummary from "./PrintableOrderSummary";
import EditablePrintPreview from "./EditablePrintPreview";
import EditOrderDialog from "./EditOrderDialog";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useReceiptCustomization } from "@/hooks/useReceiptCustomization";
import { getCountryName } from "@/utils/countryUtils";

interface OrderDetailsProps {
  order: EtsyOrder;
  onOrderUpdate?: (updatedOrder: EtsyOrder) => void;
}

const OrderDetails = ({ order, onOrderUpdate }: OrderDetailsProps) => {
  const [showEditPreview, setShowEditPreview] = useState(false);
  const { customization } = useReceiptCustomization(order.receiptId);

  // Helper function to get all product names
  const getProductNames = (order: EtsyOrder): string => {
    if (order.items && order.items.length > 0) {
      return order.items.map(item => item.title).join('; ');
    }
    return order.productName;
  };

  // Helper function to get product label (singular or plural)
  const getProductLabel = (order: EtsyOrder): string => {
    if (order.items && order.items.length > 1) {
      return "Products";
    }
    return "Product";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Processing":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "Shipped":
        return "bg-green-50 text-green-700 border-green-200";
      case "Delivered":
        return "bg-gray-50 text-gray-700 border-gray-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const getCustomerSignature = (customerName: string) => {
    return localStorage.getItem(`customer_signature_${customerName}`);
  };

  const handleOrderUpdate = (updatedOrder: EtsyOrder) => {
    if (onOrderUpdate) {
      onOrderUpdate(updatedOrder);
    }
  };

  const handleEditAndPrint = (editedOrder: EtsyOrder, supplierNotes: string, customTitle: string) => {
    const customerSignature = getCustomerSignature(editedOrder.customerName);
    
    // Open a new window for better print layout control
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Order ${editedOrder.receiptId} - Print</title>
            <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
            <style>
              body {
                font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                padding: 20px;
              }
              .card {
                border: 1px solid #e2e8f0;
                border-radius: 0.5rem;
                margin-bottom: 1rem;
              }
              .card-header {
                border-bottom: 1px solid #e2e8f0;
                padding: 1rem;
                text-align: center;
                position: relative;
              }
              .card-title {
                font-size: 1.5rem;
                font-weight: 700;
              }
              .customer-signature {
                position: absolute;
                top: 1rem;
                right: 1rem;
                max-height: 60px;
                max-width: 120px;
              }
              .card-content {
                padding: 1.5rem;
              }
              .separator {
                border-top: 1px solid #e2e8f0;
                margin: 1rem 0;
              }
              .section-title {
                font-size: 1.125rem;
                font-weight: 600;
                margin-bottom: 0.75rem;
              }
              .label {
                color: #4b5563;
                font-weight: 500;
                margin-bottom: 0.25rem;
              }
              .field {
                margin-bottom: 1rem;
              }
              .grid-2 {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1rem;
              }
              .supplier-notes {
                border: 1px solid #e2e8f0;
                border-radius: 0.375rem;
                padding: 0.75rem;
                min-height: 100px;
                white-space: pre-wrap;
              }
              .badge {
                display: inline-block;
                padding: 0.25rem 0.5rem;
                font-size: 0.875rem;
                font-weight: 500;
                border-radius: 0.375rem;
                border: 1px solid;
              }
              .badge-status-${editedOrder.status.toLowerCase()} {
                ${editedOrder.status === 'New' ? 'background-color: #eff6ff; color: #1d4ed8; border-color: #bfdbfe;' : ''}
                ${editedOrder.status === 'Processing' ? 'background-color: #fef3c7; color: #92400e; border-color: #fde68a;' : ''}
                ${editedOrder.status === 'Shipped' ? 'background-color: #ecfdf5; color: #047857; border-color: #a7f3d0;' : ''}
                ${editedOrder.status === 'Delivered' ? 'background-color: #f3f4f6; color: #4b5563; border-color: #e5e7eb;' : ''}
              }
              @media print {
                @page { margin: 0.5cm; }
                .supplier-notes { min-height: 5cm; }
              }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="card-header">
                <div class="card-title">${customTitle}</div>
                ${customerSignature ? `<img src="${customerSignature}" alt="Customer signature" class="customer-signature" />` : ''}
                <div style="margin-top: 0.5rem;">
                  <span class="badge badge-status-${editedOrder.status.toLowerCase()}">${editedOrder.status}</span>
                </div>
              </div>
              <div class="card-content">
                <div>
                  <div class="section-title">Order Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Order ID:</div>
                      <div style="font-family: monospace;">${editedOrder.receiptId}</div>
                    </div>
                    <div class="field">
                      <div class="label">Date:</div>
                      <div>${editedOrder.date}</div>
                    </div>
                    <div class="field" style="grid-column: span 2">
                      <div class="label">${getProductLabel(editedOrder)}:</div>
                      <div style="font-weight: 600;">${getProductNames(editedOrder)}</div>
                    </div>
                    ${editedOrder.trackingId && editedOrder.trackingId !== 'N/A' ? `
                    <div class="field" style="grid-column: span 2">
                      <div class="label">Tracking Number:</div>
                      <div style="font-family: monospace; background-color: #f3f4f6; padding: 0.5rem; border-radius: 0.25rem;">
                        ${editedOrder.trackingId}
                      </div>
                    </div>
                    ` : ''}
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Customer Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Name:</div>
                      <div style="font-weight: 600;">${editedOrder.customerName}</div>
                    </div>
                    ${editedOrder.customerEmail ? `
                    <div class="field">
                      <div class="label">Email:</div>
                      <div>${editedOrder.customerEmail}</div>
                    </div>
                    ` : ''}
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Shipping Address</div>
                  <div style="font-size: 0.875rem; line-height: 1.5;">
                    <div style="font-weight: 600;">${editedOrder.customerName}</div>
                    ${editedOrder.address && editedOrder.address !== 'N/A' ? `<div>${editedOrder.address}</div>` : ''}
                    <div>${editedOrder.city}, ${editedOrder.zip}</div>
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Payment Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Total Amount:</div>
                      <div style="font-weight: 700; font-size: 1.125rem;">
                        ${editedOrder.totalAmount && editedOrder.currencyCode ? 
                          `${editedOrder.currencyCode} ${editedOrder.totalAmount.toFixed(2)}` : 
                          'N/A'}
                      </div>
                    </div>
                    <div class="field">
                      <div class="label">Payment Method:</div>
                      <div>Etsy Payment</div>
                    </div>
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Supplier Notes</div>
                  <div class="supplier-notes">${supplierNotes}</div>
                </div>
              </div>
            </div>
            <script>
              // Auto print
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  // Close after printing is done or canceled
                  window.close();
                }, 500);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
    setShowEditPreview(false);
  };

  const handleQuickPrint = () => {
    const customerSignature = getCustomerSignature(order.customerName);
    
    // Open a new window for better print layout control
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Order ${order.receiptId} - Print</title>
            <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
            <style>
              body {
                font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                padding: 20px;
              }
              .card {
                border: 1px solid #e2e8f0;
                border-radius: 0.5rem;
                margin-bottom: 1rem;
              }
              .card-header {
                border-bottom: 1px solid #e2e8f0;
                padding: 1rem;
                text-align: center;
                position: relative;
              }
              .card-title {
                font-size: 1.5rem;
                font-weight: 700;
              }
              .customer-signature {
                position: absolute;
                top: 1rem;
                right: 1rem;
                max-height: 60px;
                max-width: 120px;
              }
              .card-content {
                padding: 1.5rem;
              }
              .separator {
                border-top: 1px solid #e2e8f0;
                margin: 1rem 0;
              }
              .section-title {
                font-size: 1.125rem;
                font-weight: 600;
                margin-bottom: 0.75rem;
              }
              .label {
                color: #4b5563;
                font-weight: 500;
                margin-bottom: 0.25rem;
              }
              .field {
                margin-bottom: 1rem;
              }
              .grid-2 {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1rem;
              }
              .supplier-notes {
                border: 1px solid #e2e8f0;
                border-radius: 0.375rem;
                padding: 0.75rem;
                min-height: 100px;
              }
              .badge {
                display: inline-block;
                padding: 0.25rem 0.5rem;
                font-size: 0.875rem;
                font-weight: 500;
                border-radius: 0.375rem;
                border: 1px solid;
              }
              .badge-status-${order.status.toLowerCase()} {
                ${order.status === 'New' ? 'background-color: #eff6ff; color: #1d4ed8; border-color: #bfdbfe;' : ''}
                ${order.status === 'Processing' ? 'background-color: #fef3c7; color: #92400e; border-color: #fde68a;' : ''}
                ${order.status === 'Shipped' ? 'background-color: #ecfdf5; color: #047857; border-color: #a7f3d0;' : ''}
                ${order.status === 'Delivered' ? 'background-color: #f3f4f6; color: #4b5563; border-color: #e5e7eb;' : ''}
              }
              @media print {
                @page { margin: 0.5cm; }
                .supplier-notes { min-height: 5cm; }
              }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="card-header">
                <div class="card-title">${customization.title}</div>
                ${customerSignature ? `<img src="${customerSignature}" alt="Customer signature" class="customer-signature" />` : ''}
                <div style="margin-top: 0.5rem;">
                  <span class="badge badge-status-${order.status.toLowerCase()}">${order.status}</span>
                </div>
              </div>
              <div class="card-content">
                <div>
                  <div class="section-title">Order Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Order ID:</div>
                      <div style="font-family: monospace;">${order.receiptId}</div>
                    </div>
                    <div class="field">
                      <div class="label">Date:</div>
                      <div>${order.date}</div>
                    </div>
                    <div class="field" style="grid-column: span 2">
                      <div class="label">${getProductLabel(order)}:</div>
                      <div style="font-weight: 600;">${getProductNames(order)}</div>
                    </div>
                    ${order.trackingId && order.trackingId !== 'N/A' ? `
                    <div class="field" style="grid-column: span 2">
                      <div class="label">Tracking Number:</div>
                      <div style="font-family: monospace; background-color: #f3f4f6; padding: 0.5rem; border-radius: 0.25rem;">
                        ${order.trackingId}
                      </div>
                    </div>
                    ` : ''}
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Customer Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Name:</div>
                      <div style="font-weight: 600;">${order.customerName}</div>
                    </div>
                    ${order.customerEmail ? `
                    <div class="field">
                      <div class="label">Email:</div>
                      <div>${order.customerEmail}</div>
                    </div>
                    ` : ''}
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Shipping Address</div>
                  <div style="font-size: 0.875rem; line-height: 1.5;">
                    <div style="font-weight: 600;">${order.customerName}</div>
                    ${order.address && order.address !== 'N/A' ? `<div>${order.address}</div>` : ''}
                    <div>
                      ${order.city ? order.city : ''}${order.city && order.country ? ', ' : ''}${order.country ? getCountryName(order.country) : ''}${(order.city || order.country) && order.zip ? ', ' : ''}${order.zip ? order.zip : ''}
                    </div>
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Payment Information</div>
                  <div class="grid-2">
                    <div class="field">
                      <div class="label">Total Amount:</div>
                      <div style="font-weight: 700; font-size: 1.125rem;">
                        ${order.totalAmount && order.currencyCode ? 
                          `${order.currencyCode} ${order.totalAmount.toFixed(2)}` : 
                          'N/A'}
                      </div>
                    </div>
                    <div class="field">
                      <div class="label">Payment Method:</div>
                      <div>Etsy Payment</div>
                    </div>
                  </div>
                </div>
                
                <div class="separator"></div>
                
                <div>
                  <div class="section-title">Supplier Notes</div>
                  <div class="supplier-notes"></div>
                </div>
              </div>
            </div>
            <script>
              // Auto print
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  // Close after printing is done or canceled
                  window.close();
                }, 500);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <>
      <Card className="mt-4 border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <span className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Details - {order.receiptId}
            </span>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:justify-center">
              <Badge variant="outline" className={getStatusColor(order.status)}>
                {order.status}
              </Badge>
              <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-center">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <PrintableOrderSummary order={order} />
                  </DialogContent>
                </Dialog>
                <EditOrderDialog 
                  order={order} 
                  onOrderUpdate={onOrderUpdate} 
                />
                <Button size="sm" variant="outline" onClick={() => setShowEditPreview(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit & Print
                </Button>
                <Button size="sm" variant="outline" onClick={handleQuickPrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  Quick Print
                </Button>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Customer Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{order.customerName}</p>
              </div>
              {order.customerEmail && (
                <div>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email
                  </p>
                  <p className="font-medium">{order.customerEmail}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Shipping Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Shipping Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Full Address</p>
                <p className="font-medium">
                  {order.address && order.address !== 'N/A' ? order.address : 'Address not provided'}
                </p>
                <p className="font-medium">
                  {order.city}
                  {order.city && order.country && ', '}
                  {order.country && getCountryName(order.country)}
                  {(order.city || order.country) && order.zip && ', '}
                  {order.zip}
                </p>
              </div>
              {order.trackingId && order.trackingId !== 'N/A' && (
                <div>
                  <p className="text-sm text-gray-600">Tracking Number</p>
                  <p className="font-mono text-sm bg-gray-100 p-2 rounded">{order.trackingId}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Order Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Order Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">
                  {order.items && order.items.length > 1 ? 'Products' : 'Product'}
                </p>
                {order.items && order.items.length > 0 ? (
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <p key={index} className="font-medium">
                        {index + 1}. {item.title}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium">{order.productName}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Order Date
                </p>
                <p className="font-medium">{order.date}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Payment Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="font-bold text-lg text-green-600">
                  {order.totalAmount && order.currencyCode 
                    ? `${order.currencyCode} ${order.totalAmount.toFixed(2)}`
                    : 'N/A'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium">Etsy Payment</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Preview Dialog */}
      {showEditPreview && (
        <Dialog open={showEditPreview} onOpenChange={setShowEditPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <EditablePrintPreview
              order={order}
              onPrint={handleEditAndPrint}
              onCancel={() => setShowEditPreview(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default OrderDetails;
